import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { createVendor, updateVendor } from 'services/vendor-service';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

/**
 * VendorForm Component
 * Form for creating/editing vendors with automatic risk tier calculation
 */
export const VendorForm = ({ vendor, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);
  const { t } = useTranslation(['vendor', 'common']);
  const isEditMode = !!vendor;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactName: '',
    relationshipDescription: '',
    dataSensitivity: 1,
    businessCriticality: 1,
    dpaEstablished: false,
    recertificationPeriodMonths: '',
  });

  // Calculated fields
  const [inherentRiskScore, setInherentRiskScore] = useState(1);
  const [tier, setTier] = useState(3);

  // Initialize form with vendor data if editing
  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        contactEmail: vendor.contactEmail || '',
        contactName: vendor.contactName || '',
        relationshipDescription: vendor.relationshipDescription || '',
        dataSensitivity: vendor.dataSensitivity || 1,
        businessCriticality: vendor.businessCriticality || 1,
        dpaEstablished: vendor.dpaEstablished || false,
        recertificationPeriodMonths: vendor.recertificationPeriodMonths || '',
      });
    }
  }, [vendor]);

  // Calculate inherent risk score and tier
  useEffect(() => {
    const riskScore = Math.max(formData.dataSensitivity, formData.businessCriticality);
    setInherentRiskScore(riskScore);

    // Tier assignment logic: 1-2 → Tier 3, 3-4 → Tier 2, 5 → Tier 1
    let calculatedTier;
    if (riskScore <= 2) {
      calculatedTier = 3;
    } else if (riskScore <= 4) {
      calculatedTier = 2;
    } else {
      calculatedTier = 1;
    }
    setTier(calculatedTier);
  }, [formData.dataSensitivity, formData.businessCriticality]);

  // Mutation for creating/updating vendor
  const mutation = useMutation(
    (data) => {
      if (isEditMode) {
        return updateVendor(vendor._id, data);
      } else {
        return createVendor(data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vendors');
        SwalReact.fire({
          icon: 'success',
          title: isEditMode ? t('vendor:notifications.updatedTitle') : t('vendor:notifications.createdTitle'),
          text: t('vendor:notifications.savedText', { action: isEditMode ? t('vendor:notifications.updatedTitle') : t('vendor:notifications.createdTitle') }),
          timer: 2000,
          showConfirmButton: false
        });
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('vendor:notifications.errorTitle'),
          text: error.response?.data?.message || t('vendor:notifications.saveError'),
        });
      }
    }
  );

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.dataSensitivity >= 4 && !formData.dpaEstablished) {
      const result = await SwalReact.fire({
        icon: 'warning',
        title: t('vendor:notifications.noDpaTitle'),
        html: [
          t('vendor:notifications.noDpaDataDesc', { level: formData.dataSensitivity }),
          t('vendor:notifications.noDpaLegalReq'),
          t('vendor:notifications.noDpaQuestion'),
        ].join('<br/><br/>'),
        showCancelButton: true,
        confirmButtonText: t('vendor:notifications.noDpaConfirm'),
        cancelButtonText: t('vendor:notifications.noDpaCancel'),
        confirmButtonColor: '#d33',
      });

      if (!result.isConfirmed) return;
    }

    // Add calculated fields
    const submitData = {
      ...formData,
      inherentRiskScore,
      tier,
      active: true,
      recertificationPeriodMonths: formData.recertificationPeriodMonths
        ? parseInt(formData.recertificationPeriodMonths, 10)
        : undefined,
    };

    mutation.mutate(submitData);
  };

  const getTierColor = (tierValue) => {
    switch (tierValue) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'success';
      default: return 'default';
    }
  };

  const getTierLabel = (tierValue) => {
    const key = String(tierValue);
    return t(`vendor:tier.${key}.fullLabel`, { defaultValue: t('vendor:tier.unknown.fullLabel') });
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? t('vendor:form.title.edit') : t('vendor:form.title.add')}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Vendor Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('vendor:form.sections.info')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label={t('vendor:form.fields.name')}
                value={formData.name}
                onChange={handleInputChange('name')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="email"
                label={t('vendor:form.fields.contactEmail')}
                value={formData.contactEmail}
                onChange={handleInputChange('contactEmail')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('vendor:form.fields.contactName')}
                value={formData.contactName}
                onChange={handleInputChange('contactName')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('vendor:form.fields.relationshipDescription')}
                value={formData.relationshipDescription}
                onChange={handleInputChange('relationshipDescription')}
                placeholder={t('vendor:form.fields.relationshipPlaceholder')}
              />
            </Grid>

            {/* Risk Classification */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('vendor:form.sections.risk')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>{t('vendor:form.fields.dataSensitivity')}</InputLabel>
                <Select
                  value={formData.dataSensitivity}
                  onChange={handleInputChange('dataSensitivity')}
                  label={t('vendor:form.fields.dataSensitivity')}
                >
                  <MenuItem value={1}>{t('vendor:form.dataSensitivity.1')}</MenuItem>
                  <MenuItem value={2}>{t('vendor:form.dataSensitivity.2')}</MenuItem>
                  <MenuItem value={3}>{t('vendor:form.dataSensitivity.3')}</MenuItem>
                  <MenuItem value={4}>{t('vendor:form.dataSensitivity.4')}</MenuItem>
                  <MenuItem value={5}>{t('vendor:form.dataSensitivity.5')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>{t('vendor:form.fields.businessCriticality')}</InputLabel>
                <Select
                  value={formData.businessCriticality}
                  onChange={handleInputChange('businessCriticality')}
                  label={t('vendor:form.fields.businessCriticality')}
                >
                  <MenuItem value={1}>{t('vendor:form.businessCriticality.1')}</MenuItem>
                  <MenuItem value={2}>{t('vendor:form.businessCriticality.2')}</MenuItem>
                  <MenuItem value={3}>{t('vendor:form.businessCriticality.3')}</MenuItem>
                  <MenuItem value={4}>{t('vendor:form.businessCriticality.4')}</MenuItem>
                  <MenuItem value={5}>{t('vendor:form.businessCriticality.5')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Auto-calculated Risk Tier */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography>
                    <strong>{t('vendor:form.inherentRiskScore')}</strong> {inherentRiskScore}
                  </Typography>
                  <Chip
                    label={getTierLabel(tier)}
                    color={getTierColor(tier)}
                    size="small"
                  />
                </Box>
              </Alert>
            </Grid>

            {/* Compliance */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('vendor:form.sections.compliance')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.dpaEstablished}
                    onChange={handleCheckboxChange('dpaEstablished')}
                  />
                }
                label={t('vendor:form.fields.dpaCheckbox')}
              />
            </Grid>

            {formData.dataSensitivity >= 4 && !formData.dpaEstablished && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {t('vendor:form.alerts.dpaWarning')}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('vendor:form.fields.recertificationPeriod')}
                value={formData.recertificationPeriodMonths}
                onChange={handleInputChange('recertificationPeriodMonths')}
                inputProps={{ min: 1, max: 60 }}
                helperText={t('vendor:form.fields.recertificationHelp')}
              />
            </Grid>

            {/* Form Actions */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                {onCancel && (
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={mutation.isLoading}
                  >
                    {t('common:buttons.cancel')}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={mutation.isLoading}
                >
                  {mutation.isLoading
                    ? t('vendor:form.actions.saving')
                    : isEditMode
                      ? t('vendor:form.actions.update')
                      : t('vendor:form.actions.create')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};
